import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import { Dialog, DialogPanel } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { api, ApiError } from '~/lib/api';

export interface CreateGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional legacy link; room access is granted via Share room permissions. */
  roomId?: string;
  /** e.g. room name — shown under the title when roomId is set */
  roomLabel?: string;
  onCreated?: () => void;
}

export function CreateGroupModal({
  open,
  onOpenChange,
  roomId,
  roomLabel,
  onCreated
}: CreateGroupModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: { name: string; roomId?: string }) =>
      api.post('/api/groups', body),
    onSuccess: () => {
      setName('');
      setError(null);
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
      onCreated?.();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Failed to create group');
    }
  });

  const titleId = React.useId();
  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPanel aria-labelledby={titleId}>
        <h2
          id={titleId}
          className="text-lg font-semibold leading-none tracking-tight"
        >
          Create group
        </h2>
        {roomLabel ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Add a named group{roomId ? ` (linked to ${roomLabel})` : ''}. Grant
            room access from Share room when you are ready.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Add a named group. Grant room access from Share room when you are
            ready.
          </p>
        )}

        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!canSubmit) return;
            const body: { name: string; roomId?: string } = {
              name: name.trim()
            };
            if (roomId) body.roomId = roomId;
            create.mutate(body);
          }}
        >
          <div className="space-y-2">
            <label htmlFor="create-group-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="create-group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. External counsel"
              disabled={create.isPending}
              autoComplete="off"
              data-1p-ignore
              data-form-type="other"
              autoFocus
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}
